import {Component, forwardRef, Input,} from '@angular/core';
import {FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {SearchForCompanyComponent} from '../search-for-company/search-for-company.component';
import {CustomValidators} from '../validator/customValidators';
import {Company} from '../model/company.model';
import {CustomerService} from '../../core/services/customer.service';
import {filter, map, switchMap} from 'rxjs/operators';
import {SnackBarService} from '../../core/services/snack-bar.service';
import {DictionaryPosition} from '../model/dictionary.model';
import {IEmail} from '../model/interface/email.model.interface';
import {IPhone} from '../model/interface/phone.model.interface';
import {IEditCompany} from '../model/interface/edit-company.model.interface';
import {DialogOpener} from '../utils/dialog-opener';
import {AddCustomerAddressComponent} from '../add-customer-address/add-customer-address.component';
import {AddCustomerEmailComponent} from '../add-customer-email/add-customer-email.component';
import {AddCustomerPhoneComponent} from '../add-customer-phone/add-customer-phone.component';
import {AbstractControlComponent} from '../abstract-control-value-accessor/abstract-control.component';
import {IAddress} from '../model/interface/address.model.interface';
import {AddLessorGroupComponent} from "../dialog/add-lessor-group/add-lessor-group.component";
import {AddPrivatePersonComponent} from '../dialog/add-private-person/add-private-person.component';
import {AddCompanyComponent} from '../dialog/add-company/add-company.component';
import {SimpleSelectDialogComponent} from '../dialog/simple-select-dialog/simple-select-dialog.component';
import {ObjectListHelper} from '../helper/object-list.helper';
import {ICivilPartnershipPartner, LegalEntity} from '../model/legal-entity.model';
import {EditLegalEntityComponent} from '../dialog/edit-legal-entity/edit-legal-entity.component';
import {PrivatePerson} from '../model/private-person.model';

@Component({
  selector: 'app-company-info',
  templateUrl: './company-info.component.html',
  styleUrls: ['./company-info.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanyInfoComponent),
      multi: true,
    },
  ],
})
export class CompanyInfoComponent extends AbstractControlComponent {

  constructor(private formBuilder: FormBuilder,
              private gusDialog: MatDialog,
              private dialogOpener: DialogOpener,
              private snackBarService: SnackBarService,
              private customerService: CustomerService) {
    super();
    this.initForm();
  }

  @Input() company: Company;
  @Input() readonly;
  @Input() standalone = false;
  @Input() vindicationCostsEquationView = true;
  @Input() requiredContact = false;
  companyForm: FormGroup;
  civilPartnershipPartners: ICivilPartnershipPartner[] = [];

  ngOnInit() {
    this.fillWithInitialData();
  }

  private fillWithInitialData() {
    if (!this.company) {
      return;
    }

    this.companyForm.patchValue(
      {
        id: this.company.id,
        nip: this.company.nip,
        regon: this.company.regon,
        krs: this.company.krs,
        companyName: this.company.companyName,
        individualNumber: this.company.individualNumber,
        legalForm: this.company.legalForm,
        civilPartnershipPartnerIds: this.company.civilPartnershipPartners.map(partner => partner.id)
      }
    );
    this.civilPartnershipPartners = [...this.company.civilPartnershipPartners];
  }

  deleteAddress(addressToDelete: IAddress) {
    const index = this.company.addresses
      .data
      .findIndex(ad => ad.id === addressToDelete.id);
    this.company.addresses.data.splice(index,1)
  }

  writeValue(company: Company) {
    this.company = company;
    if (company === null || company === undefined) {
      this.companyForm.reset();
    } else {
      this.updateForm();
    }
  }

  registerOnChange(fn: any): void {
    this.companyForm.valueChanges.subscribe(fn);
  }



  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.companyForm.disable() : this.companyForm.enable();
  }

  updateCompany() {
    this.persistCompany();
  }

  private persistCompany(): void {
    this.customerService.updateCompany(this.getUpdateCompanyPayload())
      .subscribe(
        (company: Company) => {
          this.snackBarService.display(DictionaryPosition.COMPANY_INFO_CHANGED);
          this.company = company;
          this.civilPartnershipPartners = [...company.civilPartnershipPartners];
          this.companyForm.patchValue({
            legalForm: company.legalForm,
            civilPartnershipPartnerIds: this.civilPartnershipPartners.map(partner => partner.id)
          });
        }
      );
  }

  private initForm() {
    this.companyForm = this.formBuilder.group({
      id: [],
      companyName: [null, [Validators.required]],
      nip: [null, CustomValidators.NIPValidator],
      regon: [null, CustomValidators.REGONValidator],
      krs: [null, CustomValidators.KRSValidator],
      individualNumber: [null],
      legalForm: ['STANDARD'],
      civilPartnershipPartnerIds: [[]]
    });
  }

  findInGus() {
    const dialogRef = this.gusDialog.open(SearchForCompanyComponent);
    return dialogRef
      .afterClosed()
      .pipe(filter(company => company !== undefined))
      .subscribe((company: any) => {
        this.companyForm.patchValue(
          {
            nip: company.nip,
            regon: company.regon,
            krs: company.krs,
            companyName: company.companyName
          }
        );
      });
  }

  private updateForm() {
    if (!this.company) {
      return;
    }

    this.companyForm.patchValue(
      {
        id: this.company.id,
        nip: this.company.nip,
        regon: this.company.regon,
        krs: this.company.krs,
        companyName: this.company.companyName,
        individualNumber: this.company.individualNumber,
        legalForm: this.company.legalForm,
        civilPartnershipPartnerIds: this.company.civilPartnershipPartners.map(partner => partner.id)
      }
    );
    this.civilPartnershipPartners = [...this.company.civilPartnershipPartners];
  }

  deleteEmail(emailToDelete: IEmail) {
    const index = this.company.emails
      .data
      .findIndex(el => el.id === emailToDelete.id);
    this.company.emails.data.splice(index, 1);
  }

  deletePhone(phoneToDelete: IPhone) {
    const index = this.company.phones
      .data
      .findIndex(el => el.id === phoneToDelete.id);
    this.company.phones.data.splice(index, 1);
  }

  addAddress() {
    return this.dialogOpener.open(AddCustomerAddressComponent, {
      data: this.company,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }

  addEmail() {
    return this.dialogOpener.open(AddCustomerEmailComponent, {
      data: this.company,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }

  addPhone() {
    return this.dialogOpener.open(AddCustomerPhoneComponent, {
      data: this.company,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }

  private getUpdateCompanyPayload(): IEditCompany {
    const value = this.companyForm.value;
    return {
      id: this.company.id,
      companyName: value.companyName,
      nip: value.nip,
      regon: value.regon,
      krs: value.krs,
      individualNumber: value.individualNumber,
      legalForm: value.legalForm,
      civilPartnershipPartnerIds: value.civilPartnershipPartnerIds,
      civilPartnershipSoleProprietorPartnerIds: this.civilPartnershipPartners
        .filter(partner => partner.companyType === 'NATURAL_PERSON')
        .map(partner => partner.id)
    };
  }

  get isCivilPartnership(): boolean {
    return this.companyForm.get('legalForm').value === 'CIVIL_PARTNERSHIP';
  }

  addCivilPartnershipPartner(): void {
    this.dialogOpener.getNonNullDialogResponseObservable(SimpleSelectDialogComponent, {
      data: {
        title: 'Wybierz typ wspólnika',
        label: 'Typ wspólnika',
        data: ObjectListHelper.fromArray([
          {label: 'Osoba fizyczna', value: 'NATURAL_PERSON'},
          {label: 'Jednoosobowa działalność gospodarcza', value: 'SOLE_PROPRIETOR'}
        ])
      }
    }).pipe(
      switchMap(type => this.dialogOpener.open(type === 'NATURAL_PERSON' ? AddPrivatePersonComponent : AddCompanyComponent, {
        data: {
          lessorId: this.company.lessorId,
          requiredContact: false,
          companyType: type === 'SOLE_PROPRIETOR' ? 'NATURAL_PERSON' : undefined
        },
        width: '80vh'
      }).afterClosed().pipe(map(partner => ({type, partner})))),
      filter(result => !!result.partner)
    ).subscribe(({type, partner}) => {
      this.civilPartnershipPartners = [...this.civilPartnershipPartners, {
        id: partner.id,
        companyName: partner.companyName,
        companyType: type === 'SOLE_PROPRIETOR' ? 'NATURAL_PERSON' : undefined,
        name: partner.name,
        secondName: partner.secondName,
        surname: partner.surname,
        pesel: partner.pesel
      }];
      this.companyForm.get('civilPartnershipPartnerIds').setValue(this.civilPartnershipPartners.map(item => item.id));
      this.persistCompany();
    });
  }

  removeCivilPartnershipPartner(partnerId: number): void {
    this.civilPartnershipPartners = this.civilPartnershipPartners.filter(partner => partner.id !== partnerId);
    this.companyForm.get('civilPartnershipPartnerIds').setValue(this.civilPartnershipPartners.map(partner => partner.id));
    this.persistCompany();
  }

  editCivilPartnershipPartner(partnerId: number): void {
    this.customerService.getOne(partnerId)
      .subscribe(partner => this.dialogOpener.open(EditLegalEntityComponent, {
        data: partner,
        width: '80vh'
      }).afterClosed().subscribe(() => this.refreshCivilPartnershipPartner(partnerId)));
  }

  private refreshCivilPartnershipPartner(partnerId: number): void {
    this.customerService.getOne(partnerId)
      .subscribe(partner => {
        const existingPartner = this.civilPartnershipPartners.find(item => item.id === partnerId);
        const refreshedPartner = this.mapCivilPartnershipPartner(partner, existingPartner?.companyType);
        this.civilPartnershipPartners = this.civilPartnershipPartners
          .map(item => item.id === partnerId ? refreshedPartner : item);
      });
  }

  private mapCivilPartnershipPartner(partner: LegalEntity,
                                     companyType?: 'LEGAL_ENTITY' | 'NATURAL_PERSON'): ICivilPartnershipPartner {
    if (partner instanceof Company) {
      return {id: partner.id, companyName: partner.companyName, companyType} as ICivilPartnershipPartner;
    }
    const privatePerson = partner as PrivatePerson;
    return {
      id: privatePerson.id,
      companyType,
      name: privatePerson.name,
      secondName: privatePerson.secondName,
      surname: privatePerson.surname,
      pesel: privatePerson.pesel
    };
  }


  create() {
    this.dialogOpener.open(AddLessorGroupComponent).afterClosed()
      .subscribe(id => {});
  }
}
